import { validateMeasuredChallenge } from "../challenge/measured-validation.js";
import { setHoleBonus } from "../contest/hole-bonus-command.js";
import { validateRoundDate } from "./round-date.js";
import { setMeasuredAttempt } from "../challenge/measured-scoring.js";
import type {
  CreateRoundCommandPayload,
  RoundCommandEvent,
  RoundCommandPayload,
} from "./round-commands.js";
export type {
  CreateRoundCommandPayload,
  RoundCommandEvent,
  RoundCommandPayload,
  RoundCommandPayloadMap,
  RoundCommandType,
} from "./round-commands.js";
import { legacyPlayingHandicap as playingHandicap } from "./round-handicap.js";
import { applyContestCommand } from "../contest/contest-commands.js";
import {
  setHoleEntry,
  transferScorekeeper,
  startV3Round,
} from "./round-v3-commands.js";
import { calculateRoundResult, RoundResultError } from "./round-result.js";
import { RoundCommandError } from "./round-errors.js";
import { isRoundV3 } from "./round-types.js";
import { validateV3Input } from "./round-v3-validation.js";
import { validateSimulatorSettings } from "../simulator/simulator-settings.js";
import { selectHoleEntry } from "./round-selectors.js";
import { selectContestProgress } from "../contest/contest-result.js";
import { reviseProvisionalHole } from "./round-score-revision.js";
import type {
  CreateRoundSnapshotInput,
  RoundParticipantInput,
  RoundParticipantSnapshot,
  RoundRulesSnapshot,
  RoundSnapshot,
  RoundState,
} from "./round-types.js";

export function createRoundSnapshot(
  input: CreateRoundSnapshotInput,
): RoundSnapshot {
  if (input.schemaVersion !== undefined) {
    validateV3Input(input);
    input = structuredClone(input);
  } else if (
    input.importedTotals ||
    input.mainContest ||
    input.orderedHoleNumbers ||
    input.scorekeeperGeneration !== undefined ||
    input.participants.some(
      (participant) => participant.teeSnapshot || participant.handicapSnapshot,
    )
  ) {
    throw new RoundCommandError(
      "Versioned round facts require schemaVersion 3.",
    );
  }
  validateDraft(input);
  input = structuredClone(input);
  const participants = input.participants.map((participant) => ({
    ...participant,
    playingHandicap: input.importedTotals
      ? input.importedTotals.players[participant.id]!.playingHandicap
      : input.schemaVersion === 3
        ? participant.handicapSnapshot!.playingHandicap
        : playingHandicap(participant, input.course, input.rules),
  }));
  const scoreTargetIds = scoringTargets(participants, input.rules);
  return {
    ...(input.importedTotals ? { importedTotals: input.importedTotals } : {}),
    roundId: input.roundId,
    scorekeeperId: input.scorekeeperId,
    course: input.course,
    conditions: input.conditions,
    rules: input.rules,
    participants,
    state: "draft",
    version: 0,
    calculationVersion: input.schemaVersion === 3 ? "3" : "2",
    ...(input.schemaVersion === 3
      ? {
          schemaVersion: 3,
          orderedHoleNumbers: input.orderedHoleNumbers!,
          scorekeeperGeneration: 0,
          course: {
            ...input.course,
            holes: input.orderedHoleNumbers!.map((number) =>
              input.course.holes.find((hole) => hole.number === number)!,
            ),
          },
          mainContest: { ...input.mainContest!, result: null },
          holeEntries: Object.fromEntries(scoreTargetIds.map((id) => [id, {}])),
        }
      : {}),
    scores: Object.fromEntries(scoreTargetIds.map((id) => [id, {}])),
    metrics: Object.fromEntries(participants.map((item) => [item.id, null])),
    declaredWinnerIds: [],
    result: null,
  };
}

export function createRoundFromCommand(
  command: RoundCommandEvent,
  scorekeeperId: string,
): RoundSnapshot {
  validateCreateEnvelope(command);
  if (!isCreatePayload(command.payload)) {
    throw new RoundCommandError("Round creation payload is incomplete.");
  }
  return createRoundSnapshot({
    ...command.payload,
    roundId: command.roundId,
    scorekeeperId,
  });
}

export function applyRoundCommand(
  snapshot: RoundSnapshot,
  command: RoundCommandEvent,
): RoundSnapshot {
  validateEnvelope(snapshot, command);
  if (
    snapshot.importedTotals &&
    !["round.started", "round.finished", "round.abandoned"].includes(
      command.type,
    )
  )
    throw new RoundCommandError(
      "Imported totals have no live hole entries. Their original reviewed result is read-only.",
    );
  switch (command.type) {
    case "round.created":
      throw new RoundCommandError("A created round already exists.");
    case "round.started":
      return startRound(snapshot, command.occurredAt);
    case "score.set":
      return setScore(snapshot, command.payload);
    case "hole.entry.set":
      return setHoleEntry(snapshot, command.payload);
    case "hole.entry.revised":
      return reviseProvisionalHole(snapshot, command.payload);
    case "contest.hole-bonus.set":
      return setHoleBonus(snapshot, command.payload);
    case "scorekeeper.transferred":
      return transferScorekeeper(snapshot, command.payload);
    case "contest.hole.conceded":
    case "contest.match.conceded":
    case "contest.remainder.agreed":
      return applyContestCommand(snapshot, command);
    case "measured.attempt.set":
      return setMeasuredAttempt(snapshot, command.payload);
    case "metric.set":
      return setMetric(snapshot, command.payload);
    case "result.declared":
      return declareResult(snapshot, command.payload);
    case "round.finished":
      return finishRound(snapshot);
    case "round.abandoned":
      return abandonRound(snapshot);
  }
}

function validateDraft(input: CreateRoundSnapshotInput): void {
  validateRoundDate(input.conditions);
  if (input.participants.length < 1 || input.participants.length > 4) {
    throw new RoundCommandError("A round needs one to four participants.");
  }
  if (
    (!input.rules.measuredChallenge &&
      !input.importedTotals &&
      input.course.holes.length < 1) ||
    input.course.holes.length > 18
  ) {
    throw new RoundCommandError("A round needs one to eighteen holes.");
  }
  if (
    new Set(input.participants.map((item) => item.id)).size !==
    input.participants.length
  ) {
    throw new RoundCommandError("Round participant IDs must be unique.");
  }
  validateRules(input);
}

function validateRules(input: CreateRoundSnapshotInput): void {
  const { rules, participants, course } = input;
  validateSimulatorSettings(rules.simulator, rules.kind);
  if (
    rules.rivalrySummary !== undefined &&
    (rules.kind !== "simulator" ||
      !["simulator", "main-and-simulator", "none"].includes(
        rules.rivalrySummary,
      ))
  )
    throw new RoundCommandError(
      "Choose a valid summary for a simulator round.",
    );
  validateMeasuredChallenge(input);
  if (
    input.schemaVersion !== 3 &&
    rules.handicapMode !== "off" &&
    (!course.rating || !course.slope)
  ) {
    throw new RoundCommandError(
      "Course rating and slope are required for handicap play.",
    );
  }
  if (rules.format === "high-low" && !rules.metric) {
    throw new RoundCommandError("A measured challenge needs a metric rule.");
  }
  if (rules.format !== "high-low" && rules.metric) {
    throw new RoundCommandError(
      "Only a measured challenge can use a metric rule.",
    );
  }
  const isTeamFormat = rules.format.startsWith("team-");
  if ((rules.kind === "team") !== isTeamFormat) {
    throw new RoundCommandError(
      "Team round kind and format must be used together.",
    );
  }
  if (isTeamFormat) validateTeams(participants);
}

function validateTeams(participants: RoundParticipantInput[]): void {
  if (participants.length !== 4) {
    throw new RoundCommandError("Team play needs four participants.");
  }
  const teamA = participants.filter((item) => item.teamId === "a").length;
  const teamB = participants.filter((item) => item.teamId === "b").length;
  if (teamA !== 2 || teamB !== 2) {
    throw new RoundCommandError(
      "Team play needs two participants on each team.",
    );
  }
}

function scoringTargets(
  participants: RoundParticipantSnapshot[],
  rules: RoundRulesSnapshot,
): string[] {
  if (
    rules.format === "team-alternate-shot" ||
    rules.format === "team-scramble" ||
    rules.format === "team-greensomes"
  ) {
    return ["team:a", "team:b"];
  }
  return participants.map((item) => item.id);
}

function startRound(
  snapshot: RoundSnapshot,
  occurredAt: string,
): RoundSnapshot {
  requireState(snapshot, "draft", "start");
  if (isRoundV3(snapshot)) return startV3Round(snapshot, occurredAt);
  return nextVersion(snapshot, { state: "active" });
}

function setScore(
  snapshot: RoundSnapshot,
  payload: RoundCommandPayload,
): RoundSnapshot {
  requireState(snapshot, "active", "score");
  if (!("targetId" in payload) || !("strokes" in payload)) {
    throw new RoundCommandError("Score command payload is incomplete.");
  }
  const hole = snapshot.course.holes.find(
    (item) => item.number === payload.holeNumber,
  );
  if (!(payload.targetId in snapshot.scores) || !hole) {
    throw new RoundCommandError("Score target is not in this round.");
  }
  if (
    !Number.isInteger(payload.strokes) ||
    payload.strokes < 1 ||
    payload.strokes > 30
  ) {
    throw new RoundCommandError("Strokes must be an integer from 1 to 30.");
  }
  if (isRoundV3(snapshot)) {
    const previous = selectHoleEntry(
      snapshot,
      payload.targetId,
      payload.holeNumber,
    );
    return setHoleEntry(snapshot, {
      targetId: payload.targetId,
      holeNumber: payload.holeNumber,
      entry: { ...previous, status: "scored", strokes: payload.strokes },
    });
  }
  return nextVersion(snapshot, {
    scores: {
      ...snapshot.scores,
      [payload.targetId]: {
        ...snapshot.scores[payload.targetId],
        [payload.holeNumber]: payload.strokes,
      },
    },
  });
}

function setMetric(
  snapshot: RoundSnapshot,
  payload: RoundCommandPayload,
): RoundSnapshot {
  requireState(snapshot, "active", "set a metric");
  if (snapshot.rules.measuredChallenge)
    throw new RoundCommandError(
      "Record individual attempts for this challenge.",
    );
  if (!("participantId" in payload) || !("value" in payload)) {
    throw new RoundCommandError("Metric command payload is incomplete.");
  }
  if (
    !(payload.participantId in snapshot.metrics) ||
    !Number.isFinite(payload.value)
  ) {
    throw new RoundCommandError("Metric target or value is invalid.");
  }
  return nextVersion(snapshot, {
    metrics: { ...snapshot.metrics, [payload.participantId]: payload.value },
  });
}

function declareResult(
  snapshot: RoundSnapshot,
  payload: RoundCommandPayload,
): RoundSnapshot {
  requireState(snapshot, "active", "declare a result");
  if (snapshot.rules.measuredChallenge)
    throw new RoundCommandError(
      "This challenge settles from the agreed attempts.",
    );
  if (!("winnerParticipantIds" in payload)) {
    throw new RoundCommandError("Declared result payload is incomplete.");
  }
  const allowed = new Set(snapshot.participants.map((item) => item.id));
  const winners = [...new Set(payload.winnerParticipantIds)];
  if (winners.length < 1 || winners.some((id) => !allowed.has(id))) {
    throw new RoundCommandError("Declared winners must be in this round.");
  }
  return nextVersion(snapshot, { declaredWinnerIds: winners });
}

function finishRound(snapshot: RoundSnapshot): RoundSnapshot {
  requireState(snapshot, "active", "finish");
  try {
    if (isRoundV3(snapshot)) {
      const result = calculateRoundResult(snapshot);
      return {
        ...snapshot,
        state: "completed",
        version: snapshot.version + 1,
        result,
        mainContest: {
          ...snapshot.mainContest,
          result: selectContestProgress(snapshot).result,
        },
      };
    }
    return nextVersion(snapshot, {
      state: "completed",
      result: calculateRoundResult(snapshot),
    });
  } catch (error) {
    if (error instanceof RoundResultError) {
      throw new RoundCommandError(error.message);
    }
    throw error;
  }
}

function abandonRound(snapshot: RoundSnapshot): RoundSnapshot {
  if (snapshot.state !== "draft" && snapshot.state !== "active") {
    throw new RoundCommandError(
      "Cannot abandon a round in " + snapshot.state + " state.",
    );
  }
  return nextVersion(snapshot, { state: "abandoned" });
}

function validateCreateEnvelope(command: RoundCommandEvent): void {
  if (command.type !== "round.created" || command.baseVersion !== 0) {
    throw new RoundCommandError("The first command must create version zero.");
  }
  if (command.deviceSequence < 1) {
    throw new RoundCommandError("Device sequence must be positive.");
  }
  if (
    command.scorekeeperGeneration !== undefined &&
    command.scorekeeperGeneration !== 0
  )
    throw new RoundCommandError(
      "A new round cannot inherit an old scorekeeper generation.",
    );
}

function validateEnvelope(
  snapshot: RoundSnapshot,
  command: RoundCommandEvent,
): void {
  if (command.roundId !== snapshot.roundId) {
    throw new RoundCommandError("Command round does not match the snapshot.");
  }
  if (command.baseVersion !== snapshot.version) {
    throw new RoundCommandError(
      `Command base version ${command.baseVersion} does not match ${snapshot.version}.`,
    );
  }
  if (command.deviceSequence < 1) {
    throw new RoundCommandError("Device sequence must be positive.");
  }
  if (
    isRoundV3(snapshot) &&
    command.scorekeeperGeneration !== snapshot.scorekeeperGeneration
  ) {
    throw new RoundCommandError(
      "The scorekeeper authority changed. Reload this round before writing.",
    );
  }
  if (
    !isRoundV3(snapshot) &&
    command.scorekeeperGeneration !== undefined &&
    command.scorekeeperGeneration !== 0
  ) {
    throw new RoundCommandError(
      "Legacy rounds do not support scorekeeper generations.",
    );
  }
}

function isCreatePayload(
  payload: RoundCommandPayload,
): payload is CreateRoundCommandPayload {
  return (
    "participants" in payload &&
    "course" in payload &&
    "conditions" in payload &&
    "rules" in payload
  );
}

function requireState(
  snapshot: RoundSnapshot,
  expected: RoundState,
  action: string,
): void {
  if (snapshot.state !== expected) {
    throw new RoundCommandError(
      `Cannot ${action} a round in ${snapshot.state} state.`,
    );
  }
}

function nextVersion(
  snapshot: RoundSnapshot,
  patch: Partial<
    Pick<
      RoundSnapshot,
      "state" | "scores" | "metrics" | "declaredWinnerIds" | "result"
    >
  >,
): RoundSnapshot {
  return { ...snapshot, ...patch, version: snapshot.version + 1 };
}

export { RoundCommandError } from "./round-errors.js";
export type * from "./round-types.js";
